<template>
  <v-layout wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center mb-2>
        <v-flex xs12 sm2>
          <h1 class="headline">{{ $t('autos.title') }}</h1>
        </v-flex>
        <v-flex xs12 sm10>
          <v-layout wrap align-center justify-end>
            <export-button
              v-if="$can('auto_export')"
              class="ml-auto mt-2 mr-3"
              action-name="auto/downloadList"
              :file-name="$t('autos.title')"
              :query="getSearchParams()"
            />
            <v-btn
              v-if="$can('auto_internal_maintenance_show')"
              :to="{ name: 'internal-maintenance' }"
              color="info mt-2 mr-3"
              depressed
            >
              {{ $t('auto.internalMaintenance') }}
            </v-btn>
            <v-btn
              v-if="$can('car_show_insurance')"
              :to="{ name: 'insurance-inspection' }"
              color="success mt-2 mr-3"
              depressed
            >
              {{ $t('auto.insuranceInspection') }}
            </v-btn>
            <v-btn
              v-if="$can('car_add')"
              type="submit"
              :to="{ name: 'add-auto' }"
              color="warning mt-2"
              depressed
            >
              {{ $t('common.btns.addAuto') }}
            </v-btn>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-layout wrap align-center>
        <v-flex
          xs12
          sm12
          md7
          lg6
          mt-4
          :class="{ 'mt-4': $vuetify.breakpoint.mdAndUp }"
        >
          <v-layout wrap align-center class="search-wrapper">
            <v-text-field
              class="search"
              ref="search"
              v-model="search"
              :placeholder="$t('autosList.search.placeholder')"
              autocomplete="off"
              solo
            ></v-text-field>
            <v-btn
              type="button"
              class="search-btn warning mb-4"
              depressed
              large
              @click="searchAutos()"
            >
              {{ $t('common.btns.search') }}
            </v-btn>
          </v-layout>
        </v-flex>
        <v-flex
          xs12
          sm12
          :class="{ 'mt-4': $vuetify.breakpoint.mdAndUp }"
          class="filter-wrapper mb-4"
        >
          <div class="d-flex">
            <v-combobox
              ref="mark"
              v-model="brandId"
              class="filter"
              item-text="title"
              item-value="id"
              :label="$t('auto.fields.brand')"
              :loading="brandsLoading"
              :items="brandsList"
              :return-object="false"
              clearable
              @update:search-input="fetchBrands"
              @change="searchAutos()"
            >
              <template #selection>
                {{ filterBrandName }}
              </template>
            </v-combobox>
            <v-combobox
              v-model="modelId"
              class="filter ml-5"
              item-text="title"
              item-value="id"
              :label="$t('auto.fields.model')"
              :loading="modelsLoading"
              :items="modelsList"
              :disabled="!brandId"
              :return-object="false"
              @update:search-input="fetchModels"
              @change="searchAutos()"
            >
              <template #selection>
                {{ filterModelName }}
              </template>
            </v-combobox>
            <v-select
              :items="allAutosStatuses"
              v-model="autoStatus"
              :label="$t('common.fields.status')"
              item-text="title"
              item-value="id"
              class="filter ml-5"
              return-object
              :loading="filtersLoading"
              @change="setAutoStatus"
            ></v-select>
            <v-select
              :items="rates"
              v-model="rentRate"
              :label="$t('rates.title')"
              :placeholder="$t('rates.all')"
              item-text="name"
              item-value="id"
              class="filter ml-5"
              clearable
              :loading="filtersLoading"
              @change="searchAutos()"
            ></v-select>
            <v-select
              :items="parks"
              v-model="carPark"
              :label="$t('employees.park')"
              :placeholder="$t('parks.all')"
              item-text="name"
              item-value="id"
              class="filter ml-5"
              clearable
              :loading="filtersLoading"
              @change="searchAutos()"
            ></v-select>
            <v-select
              :items="weekendScheduleList"
              v-model="weekendSchedule"
              :label="$t('weekendScheldule.title')"
              :placeholder="$t('weekendScheldule.all')"
              item-text="name"
              item-value="id"
              class="filter ml-5"
              clearable
              :loading="filtersLoading"
              @change="searchAutos()"
            ></v-select>
          </div>
        </v-flex>
      </v-layout>
    </v-flex>
    <v-flex xs12>
      <v-data-table
        :items="autosList.items"
        :headers="headers"
        :options.sync="sortOptions"
        :loading="isLoading"
        :loading-text="$t('common.table.dataIsLoading')"
        :server-items-length="2"
        :mobile-breakpoint="100"
        class="table-custom autosList small mb-4"
        hide-default-footer
      >
        <template slot="item" slot-scope="{ item }">
          <tr>
            <td class="text-xs-left">{{ item.num }}</td>
            <td class="text-xs-left">
              <v-layout align-center justify-start>
                {{ item.id }}
              </v-layout>
            </td>
            <td class="text-xs-left">
              <v-btn :to="`/autos/${item.id}/info`" text small>
                {{ item.number }}
              </v-btn>
            </td>
            <td class="text-xs-left">{{ item.brand }}</td>
            <td class="text-xs-left">{{ item.model }}</td>
            <td class="text-xs-left">{{ item.year }}</td>
            <td class="text-xs-left">{{ item.rentDayPrice || '-' }}</td>
            <td class="text-xs-left">{{ item.investor }}</td>
            <td class="text-xs-left">{{ item.company }}</td>
            <td class="text-xs-left">
              <div>{{ item.status }}</div>
              <div v-if="item.comment">{{ item.comment }}</div>
            </td>
            <td class="text-xs-left">{{ item.park_name }}</td>
            <td class="text-xs-left rate">
              {{ item.rent_rate_name }}
              ({{ item.rate }}%)
            </td>
            <td class="text-xs-left"> {{ item.responsible_fines_name }} </td>
            <td class=""> {{ item.agreement ? $t('common.yes') : $t('common.no') }} </td>
            <td>{{ getWeekendScheduleName(item.weekendScheduleId) }}</td>
          </tr>
        </template>
        <template slot="no-data">{{ $t('common.table.noData') }}</template>
      </v-data-table>
      <div v-if="paginationPage" class="text-xs-center">
        <v-pagination
          :value="paginationPage"
          :length="paginationLength"
          @input="choosePaginatorPage($event)"
        ></v-pagination>
      </div>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import ExportButton from '@/components/shared/ExportButton.vue';

export default {
  name: 'AutosListPage',
  components: { ExportButton },
  data() {
    return {
      filtersLoading: false,
      search: '',
      sortOptions: {},
      defaultStatus: {
        id: '',
        title: this.$t('autos.allAutos'),
      },
      autoStatus: {
        id: 1,
        title: this.$t('autos.allAutos'),
      },
      rentRate: '',
      carPark: '',
      weekendSchedule: '',
      sortField: '',
      sortDirection: '',
      brandId: '',
      modelId: '',
      brandsLoading: false,
      modelsLoading: false,
      brandsList: [],
      modelsList: [],
      headers: [
        {
          text: this.$t('common.number'),
          value: 'num',
          sortable: true,
        },
        {
          text: this.$t('autosList.tableHeaders.id'),
          value: 'cars.id',
          sortable: true,
        },
        { text: this.$t('autosList.tableHeaders.number'), sortable: false },
        {
          text: this.$t('autosList.tableHeaders.brand'),
          value: 'car_brands.name',
          sortable: true,
        },
        {
          text: this.$t('autosList.tableHeaders.model'),
          value: 'car_models.name',
          sortable: true,
        },
        {
          text: this.$t('autosList.tableHeaders.year'),
          value: 'cars.year',
          sortable: true,
        },
        {
          text: this.$t('autosList.tableHeaders.dayRentPrice'),
          sortable: true,
        },
        { text: this.$t('autosList.tableHeaders.investor'), sortable: false },
        { text: this.$t('autosList.tableHeaders.company'), sortable: false },
        { text: this.$t('autosList.tableHeaders.status'), sortable: false },
        { text: this.$t('employees.park'), sortable: false },
        { text: this.$t('rates.title'), sortable: false },
        { text: this.$t('autosList.tableHeaders.responsible_fines'), sortable: false },
        { text: this.$t('autosList.tableHeaders.access_to_data'), sortable: false },
        { text: this.$t('weekendScheldule.title'), sortable: false },
      ],
    };
  },
  watch: {
    /* eslint-disable */
    '$i18n.locale': async function() {
      await this.fetchAutosProperties();
      await this.searchAutos(this.paginationPage);
      this.$vuetify.goTo(0, 'easeInOutCubic');
    },
    sortOptions: {
      async handler() {
        this.sortField = this.sortOptions.sortBy[0]
          ? this.sortOptions.sortBy[0]
          : '';
        if (this.sortOptions.sortDesc[0] !== undefined) {
          this.sortDirection = this.sortOptions.sortDesc[0] ? 'desc' : 'asc';
        } else {
          this.sortDirection = '';
        }
        await this.searchAutos();
      },
      deep: true,
    },
    brandId(val) {
      this.modelId = '';
      if (val) this.fetchModels();
    },
  },
  computed: {
    ...mapGetters({
      isLoading: 'global/getLoader',
      autosList: 'auto/getAutosListData',
      autosStatuses: 'auto/getAutosStatuses',
    }),
    allAutosStatuses() {
      return [this.defaultStatus].concat(this.autosStatuses);
    },
    paginationLength() {
      return this.autosList.lastPage;
    },
    paginationPage() {
      if (this.autosList.currentPage === 1 && this.autosList.lastPage === 1) {
        return 0;
      }
      return this.autosList.currentPage;
    },
    rates() {
      const { list } = this.$store.state.rates;

      return [{ name: this.$t('common.no-selected'), id: 'null' }, ...list];
    },
    parks() {
      const { list } = this.$store.state.parks;

      return [{ name: this.$t('common.no-selected'), id: 'null' }, ...list];
    },
    weekendScheduleList() {
      const { list } = this.$store.state['weekend-schedule'];

      return [{ name: this.$t('common.no-selected'), id: 'null' }, ...list];
    },
    filterBrandName() {
      const id = this.brandId;

      const brand = this.brandsList.find((item) => id === item.id);
      return brand ? brand.title : '';
    },
    filterModelName() {
      const id = this.modelId;

      const model = this.modelsList.find((item) => id === item.id);
      return model ? model.title : '';
    },
  },
  async created() {
    this.filtersLoading = true;
    await Promise.all([
      this.fetchAutosProperties(),
      this.$store.dispatch('rates/fetchList'),
      this.$store.dispatch('parks/fetchList'),
      this.$store.dispatch('weekend-schedule/fetchList'),
      this.fetchBrands(''),
    ]);
    this.filtersLoading = false;
  },
  destroyed() {
    this.setAutosListData({});
    this.clearAutosProperties();
    this.clearAlertError();
  },
  methods: {
    ...mapActions({
      fetchAutos: 'auto/fetchAutosListData',
      fetchAutosProperties: 'auto/fetchAutosProperties',
    }),
    ...mapMutations({
      setAutosListData: 'auto/setAutosListData',
      clearAutosProperties: 'auto/clearAutosProperties',
      clearAlertError: 'global/clearAlertError',
    }),
    getSearchParams(page = 1) {
      return {
        page,
        searchString: this.search,
        autoStatus: this.autoStatus.id.toString(),
        sortField: this.sortField,
        sortDirection: this.sortDirection,
        rentRate: this.rentRate,
        carParkId: this.carPark,
        brandId: this.brandId,
        modelId: this.modelId,
        weekendSchedule: this.weekendSchedule,
      };
    },
    async searchAutos(page = 1) {
      await this.fetchAutos(this.getSearchParams(page));
    },
    async choosePaginatorPage(page) {
      await this.searchAutos(page);
      this.$vuetify.goTo(0, 'easeInOutCubic');
    },
    async setAutoStatus() {
      await this.searchAutos();
    },
    async fetchBrands(name = '') {
      if (name === null) return;

      this.brandsLoading = true;
      this.brandsList = await this.$store.dispatch(
        'auto/fetchBrandsData',
        name,
      );
      this.brandsLoading = false;
    },
    async fetchModels(search = '') {
      if (!this.brandId) return;
      if (search === null) return;

      this.modelsLoading = true;
      this.modelsList = await this.$store.dispatch('auto/fetchModelsData', {
        brandId: this.brandId,
        search,
      });
      this.modelsLoading = false;
    },
    getWeekendScheduleName(id) {
      if (!id) return '—';

      const { list } = this.$store.state['weekend-schedule'];
      const schedule = list.find((item) => item.id === id);

      return schedule ? schedule.name : '';
    },
  },
};
</script>

<style lang="scss" scoped>
.autosList {
  ::v-deep th {
    white-space: nowrap;
  }
}
.search-wrapper {
  .search {
    margin-right: -12px;
    margin-bottom: -12px;
    max-width: 400px;

    @media (max-width: 400px) {
      margin-right: 0px;
      margin-bottom: -12px;
      max-width: 100%;
    }
  }

  @media (max-width: 400px) {
    flex-direction: column;

    .search {
      width: 100%;
    }
  }
}

.rate {
  white-space: nowrap;
}
</style>
